import type { UserRole } from "@/lib/supabase";

/**
 * Liste exhaustive des 6 rôles fonctionnels.
 * À garder synchronisée avec l'enum `public.user_role` côté Postgres.
 */
export const USER_ROLES = [
  "salarie",
  "drh",
  "consultant_aon",
  "chef_projet_aon",
  "admin_aon",
  "super_admin",
] as const satisfies readonly UserRole[];

/** Rôles internes Aon (production, pilotage, administration, super-administration). */
export const AON_INTERNAL_ROLES = [
  "consultant_aon",
  "chef_projet_aon",
  "admin_aon",
  "super_admin",
] as const satisfies readonly UserRole[];

/** Rôles externes (côté client / bénéficiaire). */
export const EXTERNAL_ROLES = ["salarie", "drh"] as const satisfies readonly UserRole[];

/** Libellés français des rôles, pour l'UI. */
export const USER_ROLE_LABELS: Record<UserRole, string> = {
  salarie: "Salarié",
  drh: "DRH",
  consultant_aon: "Consultant Aon",
  chef_projet_aon: "Chef de projet Aon",
  admin_aon: "Admin Aon",
  super_admin: "Super-admin",
};

/** Descriptions courtes des rôles (tooltip / picker). */
export const USER_ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  salarie: "Bénéficiaire du BRI",
  drh: "RH / DRH client",
  consultant_aon: "Production des BRI",
  chef_projet_aon: "Pilote opération Aon",
  admin_aon: "Administration de la plateforme",
  super_admin: "Gère les sociétés confidentielles et les super-admins",
};

// =============================================================================
// Helpers purs (sur tableau de rôles fourni)
// =============================================================================

export function hasRole(roles: readonly UserRole[] | null | undefined, role: UserRole): boolean {
  return Boolean(roles?.includes(role));
}

export function hasAnyRole(
  roles: readonly UserRole[] | null | undefined,
  candidates: readonly UserRole[],
): boolean {
  if (!roles || roles.length === 0) return false;
  return candidates.some((r) => roles.includes(r));
}

export function isAonInternal(roles: readonly UserRole[] | null | undefined): boolean {
  return hasAnyRole(roles, AON_INTERNAL_ROLES);
}

export function isSuperAdmin(roles: readonly UserRole[] | null | undefined): boolean {
  return hasRole(roles, "super_admin");
}

export function isExternal(roles: readonly UserRole[] | null | undefined): boolean {
  return hasAnyRole(roles, EXTERNAL_ROLES);
}

/** Rôle "principal" : on prend le plus privilégié pour l'affichage par défaut. */
export function primaryRole(roles: readonly UserRole[] | null | undefined): UserRole | null {
  if (!roles || roles.length === 0) return null;
  const priority: UserRole[] = [
    "admin_aon",
    "super_admin",
    "chef_projet_aon",
    "consultant_aon",
    "drh",
    "salarie",
  ];
  for (const r of priority) {
    if (roles.includes(r)) return r;
  }
  return roles[0] ?? null;
}

/** Accueil du compte cible après ouverture d'une session de diagnostic. */
export function impersonationHome(roles: readonly UserRole[]): string {
  switch (primaryRole(roles)) {
    case "admin_aon":
    case "super_admin":
      return "/app/admin";
    case "consultant_aon":
    case "chef_projet_aon":
      return "/app/aon";
    case "drh":
      return "/app/drh";
    default:
      return "/app";
  }
}

/**
 * Retourne les rôles effectifs de l'utilisateur.
 *
 * Volontairement SANS héritage : un admin n'est PAS automatiquement
 * chef de projet ni consultant. Si un utilisateur doit cumuler plusieurs
 * casquettes (ex. admin + chef projet), il faut explicitement lui attribuer
 * les rôles correspondants dans `user_roles` côté BDD.
 *
 * Ce choix évite le caractère trompeur de l'héritage automatique :
 * un admin technique ne voit pas les écrans de production sauf si on le
 * désigne aussi comme tel.
 *
 * NB : les `RoleGuard` listent explicitement tous les rôles autorisés
 * dans leur prop `allow`, donc supprimer l'héritage ne casse rien.
 */
export function expandRoles(roles: readonly UserRole[] | null | undefined): UserRole[] {
  if (!roles || roles.length === 0) return [];
  // Pas d'héritage : on retourne la liste telle quelle (dédupliquée).
  return Array.from(new Set(roles));
}
